import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigurationService } from './configuration.service';
import { filter, Observable, throwError, timeout, retry, catchError } from 'rxjs';
import { DriftConfiguration } from '../models/configuration.interface';

/**
 * CURRENT IMPLEMENTATION: LibraryDocument Interface
 *
 * This interface defines the structure of library documents as they are currently
 * returned from the backend API. The structure is designed to be compatible with
 * the AG Grid display component and matches the transformed data from the backend.
 *
 * FUTURE IMPLEMENTATION CONSIDERATIONS:
 * When the full S3 ingestion pipeline is implemented, this interface may need to be
 * extended to include:
 * - documentId: Unique identifier for the document
 * - libraryId: Reference to the parent library
 * - documentUrl: Direct link to the document in S3
 * - vectorEmbeddings: Metadata about semantic search capabilities
 * - processingStatus: Status of document processing (OCR, NLP, etc.)
 * - contentMetadata: Rich metadata extracted from document content
 * - tags: User-defined or AI-generated tags for categorization
 * - lastModified: Timestamp of last document update
 * - fileSize: Size of the original document
 * - pageCount: Number of pages in the document
 * - confidence: AI confidence scores for extracted metadata
 */
export interface LibraryDocument {
  state: string; // State abbreviation (e.g., "CA", "TX")
  utility: string; // Utility company name
  company: string; // Company name (currently same as utility)
  filingType: string; // Type of regulatory filing
  proceeding: {
    // Proceeding information
    number: string; // Unique proceeding identifier
  };
  ingestedAt: string; // ISO timestamp when document was ingested
  title: string; // Document title (generated or extracted)
  summary: string; // Document summary (generated or extracted)
  year?: number; // Year extracted from filing date
  filingDate?: string; // Original filing date
  entity?: string; // Optional entity information
  type?: string; // Optional document type
  industry?: string; // Industry classification (defaults to "Energy")
  // Document identifiers for cloning operations
  documentId?: string; // Actual document ID from database
  libraryId?: string; // Library ID this document belongs to
  fileId?: string; // File ID for this document
}

/**
 * CURRENT IMPLEMENTATION: LibraryDocumentsResponse Interface
 *
 * This interface defines the response structure from the backend API.
 * Updated to match the actual API response format with pagination metadata.
 *
 * FUTURE IMPLEMENTATION:
 * When advanced search is fully implemented, this should be extended to:
 * - facets: Aggregated filter options (states, years, filing types, etc.)
 * - searchMetadata: Information about search performance and relevance
 */
export interface LibraryDocumentsResponse {
  documents: LibraryDocument[];
  total: number; // Total number of matching documents
  limit: number; // Applied pagination limit
  offset: number; // Applied pagination offset
  has_more: boolean; // Boolean indicating if more results are available
  // TODO: Add future fields when backend supports them
  // facets?: {
  //   states: { [key: string]: number };
  //   years: { [key: string]: number };
  //   filingTypes: { [key: string]: number };
  // };
}

/**
 * Clone Documents Request Interface
 *
 * This interface defines the request structure for cloning library documents
 * to a target collection. Used by the cloneDocumentsToCollection method.
 */
export interface CloneDocumentsRequest {
  document_ids: string[]; // Array of document IDs to clone
  target_collection_id: string; // ID of the target collection
}

/**
 * Clone Documents Response Interface
 *
 * This interface defines the response structure from the clone documents API.
 * Provides feedback on the success of the clone operation.
 */
export interface CloneDocumentsResponse {
  message: string; // Success message
  cloned_count: number; // Number of documents successfully cloned
  target_collection_id: string; // ID of the target collection
  request_id?: string; // Request ID for tracking
  processing_time_seconds?: number; // Processing time in seconds
  failed_documents?: {
    document_id: string;
    error: string;
  }[]; // List of documents that failed to clone
}

/**
 * CURRENT IMPLEMENTATION: LibrariesService
 *
 * This service provides HTTP client methods for interacting with the Libraries API.
 * It handles configuration loading, URL construction, and query parameter encoding
 * for library document retrieval and filtering.
 *
 * CURRENT FUNCTIONALITY:
 * - Fetches documents from specific libraries
 * - Fetches all documents across all libraries
 * - Supports basic filtering by state, year, and text search
 * - Handles query parameter encoding and URL construction
 *
 * FUTURE IMPLEMENTATION CONSIDERATIONS:
 * When the full S3 ingestion pipeline and advanced features are implemented,
 * this service should be extended to support:
 * - Document upload and management
 * - Advanced search with semantic/vector similarity
 * - Real-time document processing status updates
 * - Document versioning and history
 * - Bulk operations (copy, move, delete)
 * - Caching for frequently accessed documents
 * - WebSocket connections for real-time updates
 * - Document preview and thumbnail generation
 * - User preferences and saved searches
 * - Export functionality (PDF, CSV, etc.)
 */
@Injectable({
  providedIn: 'root'
})
export class LibrariesService {
  config: DriftConfiguration;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {
    this.config = {};

    // Wait for configuration to load before making API calls
    // This ensures we have the correct API endpoint URL
    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });
  }

  /**
   * CURRENT IMPLEMENTATION: Get documents from a specific library
   *
   * This method fetches documents that belong to a specific library, with optional
   * filtering capabilities. It constructs query parameters for server-side filtering
   * to reduce data transfer and improve performance.
   *
   * CURRENT BEHAVIOR:
   * - Calls the `/libraries/{libraryId}/documents` endpoint
   * - Supports filtering by states (comma-separated abbreviations)
   * - Supports filtering by years (comma-separated integers)
   * - Supports text search across document fields
   * - Returns documents in the format expected by AG Grid
   *
   * FUTURE ENHANCEMENTS:
   * - Add pagination support (limit, offset parameters)
   * - Add sorting options (by date, relevance, title, etc.)
   * - Add advanced filters (filing type, utility company, etc.)
   * - Add semantic search capabilities using vector embeddings
   * - Add faceted search with aggregated filter counts
   * - Add caching with cache invalidation strategies
   * - Add request debouncing for search queries
   * - Add error handling with retry logic
   *
   * @param libraryId The ID of the library to fetch documents from
   * @param params Optional filtering parameters
   * @returns Observable of library documents response
   */
  getLibraryDocuments(
    libraryId: string,
    params?: {
      states?: string[]; // State abbreviations to filter by
      years?: string[]; // Years to filter by
      search?: string; // Text search query
      // TODO: Add future parameters when backend supports them
      // limit?: number;       // Pagination limit
      // offset?: number;      // Pagination offset
      // sortBy?: string;      // Sort field
      // sortOrder?: 'asc' | 'desc'; // Sort direction
      // filingTypes?: string[]; // Filing type filter
      // utilities?: string[];   // Utility company filter
    }
  ): Observable<LibraryDocumentsResponse> {
    let url = `${this.config.driftApi}/libraries/${libraryId}/documents`;

    // Build query parameters for server-side filtering
    // This reduces data transfer and improves performance
    const queryParams: string[] = [];

    if (params?.states && params.states.length > 0) {
      queryParams.push(`states=${params.states.join(',')}`);
    }

    if (params?.years && params.years.length > 0) {
      queryParams.push(`years=${params.years.join(',')}`);
    }

    if (params?.search && params.search.trim()) {
      // Encode search query to handle special characters
      queryParams.push(`search=${encodeURIComponent(params.search.trim())}`);
    }

    // TODO: Add future query parameters
    // if (params?.limit) queryParams.push(`limit=${params.limit}`);
    // if (params?.offset) queryParams.push(`offset=${params.offset}`);
    // if (params?.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
    // if (params?.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return this.http.get<LibraryDocumentsResponse>(url);
  }

  /**
   * CURRENT IMPLEMENTATION: Get all library documents from all libraries
   *
   * This method fetches documents from all libraries in the system, providing
   * a unified view of all available library documents. This is the primary
   * method used by the Libraries component to display the document grid.
   *
   * CURRENT BEHAVIOR:
   * - Calls the `/libraries/all-documents` endpoint
   * - Scans all library items in the DynamoDB table
   * - Transforms library items to document format on the backend
   * - Supports the same filtering options as getLibraryDocuments
   * - Returns documents in AG Grid compatible format
   *
   * BACKEND IMPLEMENTATION NOTE:
   * Currently, this endpoint scans the entire DynamoDB table and filters
   * library items (where library_id != document_id) to create document
   * records. This is a temporary solution until proper document ingestion
   * from S3 is implemented.
   *
   * FUTURE ENHANCEMENTS:
   * - Replace table scan with efficient queries when proper document structure exists
   * - Add vector similarity search for semantic document discovery
   * - Implement result ranking based on relevance and recency
   * - Add real-time updates when new documents are ingested
   * - Add personalized recommendations based on user activity
   * - Add document clustering and categorization
   * - Add export capabilities for search results
   * - Add saved search functionality
   * - Add advanced analytics and usage tracking
   *
   * @param params Optional filtering parameters
   * @returns Observable of all library documents response
   */
  getAllLibraryDocuments(params?: {
    states?: string[]; // State abbreviations to filter by
    years?: string[]; // Years to filter by
    search?: string; // Text search query across all document fields
    // TODO: Add future parameters for enhanced functionality
    // limit?: number;       // Pagination limit
    // offset?: number;      // Pagination offset
    // sortBy?: string;      // Sort field (title, date, relevance, etc.)
    // sortOrder?: 'asc' | 'desc'; // Sort direction
    // filingTypes?: string[]; // Filter by filing types
    // utilities?: string[];   // Filter by utility companies
    // industries?: string[];  // Filter by industry (when expanded beyond energy)
    // dateRange?: { start: string; end: string }; // Date range filter
    // minRelevanceScore?: number; // Minimum relevance for search results
  }): Observable<LibraryDocumentsResponse> {
    let url = `${this.config.driftApi}/libraries/all-documents`;

    // Build query parameters using the same logic as getLibraryDocuments
    // This ensures consistent filtering behavior across both methods
    const queryParams: string[] = [];

    if (params?.states && params.states.length > 0) {
      queryParams.push(`states=${params.states.join(',')}`);
    }

    if (params?.years && params.years.length > 0) {
      queryParams.push(`years=${params.years.join(',')}`);
    }

    if (params?.search && params.search.trim()) {
      // Encode search query to handle special characters and spaces
      queryParams.push(`search=${encodeURIComponent(params.search.trim())}`);
    }

    // TODO: Add future query parameters when backend supports them
    // if (params?.limit) queryParams.push(`limit=${params.limit}`);
    // if (params?.offset) queryParams.push(`offset=${params.offset}`);
    // if (params?.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
    // if (params?.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
    // if (params?.filingTypes?.length) queryParams.push(`filingTypes=${params.filingTypes.join(',')}`);
    // if (params?.utilities?.length) queryParams.push(`utilities=${params.utilities.join(',')}`);

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return this.http.get<LibraryDocumentsResponse>(url);
  }

  /**
   * Clone selected library documents to a target collection
   *
   * This method creates references to library documents in a target collection
   * without duplicating the actual document files. The cloned documents will
   * be immediately available in the target collection and included in Advisor
   * queries for that collection.
   *
   * CURRENT BEHAVIOR:
   * - Calls the `/libraries/clone-documents` endpoint
   * - Validates user permissions for the target collection
   * - Creates document references in the target collection
   * - Returns success response with clone count
   *
   * ERROR HANDLING:
   * - 400: Invalid request (missing/invalid document IDs or collection ID)
   * - 403: Permission denied (user cannot access target collection)
   * - 404: Document or collection not found
   * - 500: Internal server error during clone operation
   * - Network errors: Connection issues, timeouts
   * - Includes retry logic for transient failures
   *
   * @param documentIds Array of document IDs to clone from libraries
   * @param targetCollectionId ID of the collection to clone documents to
   * @returns Observable of clone operation response
   */
  cloneDocumentsToCollection(documentIds: string[], targetCollectionId: string): Observable<CloneDocumentsResponse> {
    // Validate input parameters
    if (!documentIds || documentIds.length === 0) {
      return throwError(() => new Error('Document IDs are required'));
    }

    if (!targetCollectionId || targetCollectionId.trim() === '') {
      return throwError(() => new Error('Target collection ID is required'));
    }

    if (documentIds.length > 100) {
      return throwError(() => new Error('Maximum 100 documents can be cloned at once'));
    }

    const url = `${this.config.driftApi}/libraries/clone-documents`;

    const requestBody: CloneDocumentsRequest = {
      document_ids: documentIds,
      target_collection_id: targetCollectionId.trim()
    };

    return this.http.post<CloneDocumentsResponse>(url, requestBody).pipe(
      // Set timeout for the request (30 seconds)
      timeout(30000),

      // Retry transient failures (network issues, 5xx errors) up to 2 times
      retry({
        count: 2,
        delay: (error: HttpErrorResponse, retryCount: number) => {
          // Only retry for network errors or server errors (5xx)
          if (error.status === 0 || (error.status >= 500 && error.status < 600)) {
            // Exponential backoff: 1s, 2s
            const delay = Math.pow(2, retryCount) * 1000;
            console.log(`Retrying clone operation in ${delay}ms (attempt ${retryCount + 1})`);
            return new Promise(resolve => setTimeout(resolve, delay));
          }
          // Don't retry client errors (4xx)
          throw error;
        }
      }),

      // Enhanced error handling
      catchError((error: HttpErrorResponse) => {
        console.error('Clone documents error:', error);

        // Add context to the error for better handling upstream
        const enhancedError = {
          ...error,
          context: 'cloneDocumentsToCollection',
          requestBody,
          timestamp: new Date().toISOString()
        };

        return throwError(() => enhancedError);
      })
    );
  }

  // TODO: Add future methods for complete Libraries functionality

  /**
   * FUTURE METHOD: Upload documents to a library
   * This will be needed when document upload functionality is implemented
   *
   * uploadDocuments(libraryId: string, files: File[]): Observable<UploadResponse> {
   *   // Implementation for document upload with progress tracking
   * }
   */

  /**
   * FUTURE METHOD: Get document download URL
   * This will provide secure access to document files
   *
   * getDocumentDownloadUrl(libraryId: string, documentId: string): Observable<{url: string}> {
   *   // Implementation for secure document access
   * }
   */

  /**
   * FUTURE METHOD: Perform semantic search
   * This will enable vector similarity search across documents
   *
   * semanticSearch(query: string, options?: SemanticSearchOptions): Observable<SemanticSearchResponse> {
   *   // Implementation for vector-based document search
   * }
   */

  /**
   * FUTURE METHOD: Get document processing status
   * This will track the status of document ingestion and processing
   *
   * getDocumentProcessingStatus(documentId: string): Observable<ProcessingStatus> {
   *   // Implementation for tracking document processing pipeline
   * }
   */
}
